import React from 'react';
import type { ReviewsWidgetCardSize, ReviewsWidgetType } from '@clareefai/types';
// The real embed script, inlined as text at build time. The preview executes it
// and calls its exposed renderer, so the dashboard shows exactly what visitors
// get on their site — one renderer, no drift.
import widgetSource from '../../../../promoter/public/widget/reviews-v1.js?raw';

export interface PreviewReview {
  id: string;
  promoterName: string;
  promoterCompany?: string | null;
  promoterPhotoUrl?: string | null;
  rating?: number | null;
  comment?: string | null;
  vendorResponse?: string | null;
  createdAt?: string | null;
}

interface ReviewsWidgetPreviewProps {
  widgetType: ReviewsWidgetType;
  cardSize: ReviewsWidgetCardSize;
  heading: string | null;
  accentColor: string | null;
  showCompanyResponse: boolean;
  ctaLabel: string | null;
  showLeaveReviewCta: boolean;
  companyName?: string | null;
  logo?: string | null;
  reviews: PreviewReview[];
  averageRating: number;
  totalReviews: number;
  /** Sample reviews have fake ids — omit review links so cards stay unlinked. */
  linkable: boolean;
  emptyLabel: string;
}

declare global {
  interface Window {
    ClareefaiReviewsWidget?: {
      version: number;
      render: (host: HTMLElement, payload: unknown, opts?: unknown) => void;
    };
  }
}

// Execute the embed script once per page. Without a script tag or a keyed mount
// div it skips auto-init and only exposes window.ClareefaiReviewsWidget.
// Safety: widgetSource is our own static file inlined at build time (?raw) —
// nothing user-controlled is ever concatenated into it.
const ensureRenderer = () => {
  if (!window.ClareefaiReviewsWidget) {
    // eslint-disable-next-line no-new-func
    new Function(widgetSource)();
  }
  return window.ClareefaiReviewsWidget;
};

/** In-page preview of the embedded reviews widget, rendered by reviews-v1.js itself. */
const ReviewsWidgetPreview: React.FC<ReviewsWidgetPreviewProps> = ({
  widgetType,
  cardSize,
  heading,
  accentColor,
  showCompanyResponse,
  ctaLabel,
  showLeaveReviewCta,
  companyName,
  logo,
  reviews,
  averageRating,
  totalReviews,
  linkable,
  emptyLabel,
}) => {
  // Same shape as ReviewsWidgetPublicPayload — what the public endpoint serves.
  // leaveReviewUrl is a placeholder: the vendor sees the element in place, and
  // there's no eventUrl so the script sends nothing.
  const payload = {
    companyName: companyName ?? null,
    logo: logo ?? null,
    widgetType,
    cardSize,
    heading,
    accentColor,
    showCompanyResponse,
    ctaLabel,
    publicSiteUrl: linkable ? 'https://clareefai.com' : null,
    vendorSlug: null,
    leaveReviewUrl: showLeaveReviewCta ? 'https://clareefai.com' : null,
    averageRating,
    totalReviews,
    reviews,
  };
  const signature = JSON.stringify(payload);

  // Ref callback, not an effect: it re-fires when the payload changes (new
  // identity) and the signature check makes repeat calls with an unchanged
  // payload a no-op. No endpoint opts → the script's CTA runs in preview mode.
  const mount = (node: HTMLDivElement | null) => {
    if (!node || node.dataset.signature === signature) return;
    const renderer = ensureRenderer();
    if (!renderer) return;
    node.dataset.signature = signature;
    node.textContent = '';
    renderer.render(node, payload);
  };

  return (
    <div className="bg-gray-50 border border-dashed border-gray-300 rounded-xl p-5">
      {reviews.length === 0 ? (
        <p className="text-sm text-gray-400 text-center py-10">{emptyLabel}</p>
      ) : (
        <div ref={mount} />
      )}
    </div>
  );
};

export default ReviewsWidgetPreview;
